import type { Option, Timescale } from "@tento-lona/sheets";
import { NaiveDate, PartialDate } from "@tento-chrono";

/**
 * Result of calculating the pixel position and width of a date span
 * relative to a reference column.
 */
export interface SpanRangeResult {
  offset: number;
  width: number;
}

/**
 * Function type for calculating the pixel position and width of a date span.
 */
export type SpanRangeCalculator = (
  anchorDate: NaiveDate,
  span: NaiveDate.Range,
) => SpanRangeResult;

/**
 * Interface for resolving a date to its column information.
 */
export interface ColumnResolver {
  /**
   * Resolves a date to its column index and date range.
   */
  resolveDate(date: NaiveDate, granularity: Timescale): ResolvedColumn;

  /**
   * Gets the width in pixels for a column at the given index.
   */
  getColumnWidthPx(colIdx: number): number;
}

/**
 * Information about a resolved column.
 */
export interface ResolvedColumn {
  /** The column index */
  colIdx: number;
  /** The date range covered by this column */
  colRange: NaiveDate.Range;
}

/**
 * Creates a SpanRangeCalculator for positioning date spans within a column header.
 *
 * This handles spans that may cross column boundaries (e.g., a week span that starts
 * in October but extends into November). Each segment of the span uses the pxPerDay
 * of its containing column.
 *
 * @param granularity - The date granularity (e.g., "month", "week", "day")
 * @param currentColumnIdx - The index of the column we're rendering into
 * @param resolver - Interface for resolving dates to columns and getting widths
 */
function makeSpanRangeCalculator(
  granularity: Timescale,
  currentColumnIdx: number,
  resolver: ColumnResolver,
): SpanRangeCalculator {
  return (_anchorDate: NaiveDate, span: NaiveDate.Range): SpanRangeResult => {
    let totalWidth = 0;
    let startOffsetPx: Option<number> = null;
    let currentDate = span.start;

    while (currentDate.isBefore(span.end)) {
      const resolved = resolver.resolveDate(currentDate, granularity);
      const colIdx = resolved.colIdx;
      const colRange = resolved.colRange;

      const colWidthPx = resolver.getColumnWidthPx(colIdx) - 4;
      const daysInCol = colRange.numDays();
      const colPxPerDay = colWidthPx / daysInCol;

      // Segment: from currentDate to min(span.end, colRange.end)
      const segEnd = span.end.isBefore(colRange.end) ? span.end : colRange.end;
      const daysInSeg = segEnd.differenceInDays(currentDate);
      const segWidthPx = daysInSeg * colPxPerDay;

      // Calculate start offset on first segment
      // Use column index difference to compute relative offset
      if (startOffsetPx == null) {
        const colIdxDiff = colIdx - currentColumnIdx;
        let relativeColOffset = 0;
        if (colIdxDiff < 0) {
          // Segment column is before current column - sum widths going backwards
          for (let i = colIdx; i < currentColumnIdx; i++) {
            relativeColOffset -= resolver.getColumnWidthPx(i);
          }
        } else if (colIdxDiff > 0) {
          // Segment column is after current column - sum widths going forwards
          for (let i = currentColumnIdx; i < colIdx; i++) {
            relativeColOffset += resolver.getColumnWidthPx(i);
          }
        }
        const dayOffsetInCol = currentDate.differenceInDays(colRange.start);
        const segOffsetInCol = dayOffsetInCol * colPxPerDay;
        startOffsetPx = relativeColOffset + segOffsetInCol;
      }

      totalWidth += segWidthPx;
      currentDate = segEnd;
    }

    return { offset: startOffsetPx ?? 0, width: totalWidth };
  };
}

/** Returns the `paddingInline` value for a header or header-sub cell based on column type. */
export function headerCellPaddingInline(columnType: string): string {
  return columnType === "day" ? "0px" : "2px";
}

/**
 * Creates a simple ColumnResolver for testing or cases where all columns
 * have the same width and follow a regular pattern.
 */
function makeSimpleColumnResolver(
  granularity: Timescale,
  columnWidthPx: number,
): ColumnResolver {
  return {
    resolveDate(date: NaiveDate, _granularity: Timescale): ResolvedColumn {
      const pd = PartialDate.fromDateAndType(granularity, date);
      const colRange = pd.toRange();
      // For simple resolver, column index is based on months from epoch
      // This is a simplification - real implementation uses mapper
      const colIdx = date.yr * 12 + date.month1;
      return { colIdx, colRange };
    },
    getColumnWidthPx(_colIdx: number): number {
      return columnWidthPx;
    },
  };
}

export namespace SpanRangeCalculator {
  export const make = makeSpanRangeCalculator;
}

export namespace ColumnResolver {
  export const makeSimple = makeSimpleColumnResolver;
}
